/*
 * Copyright (c) 2021 SARL SIGMA POLYNESIA
 */

import { useContext } from 'react'
import { useIntl } from 'react-intl'
import ButtonGroup from '../../../client/js/components/form/ButtonGroup'
import FormSaveButton from '../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../client/js/components/form/SmartForm'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../client/js/lib/form'
import { AuthContext } from '../../auth/components/AuthProvider'
import { patchRole, postRole } from '../lib/roleAPIClient'
import RoleSchema from '../schemas/RoleSchema.mjs'
import NameField from './fields/NameField'
import PermissionsField from './fields/PermissionsField'
import SlugField from './fields/SlugField'
import FormResetButton from '../../../client/js/components/form/buttons/FormResetButton'
import { getRoleListLink } from '../lib/roleLinks'
import { Stack } from '@mantine/core'
import FormSection from '../../twh/components/FormSection'
import useCustomForm from '../../../client/js/hooks/useCustomForm'

export type RoleFormProps = {
  initialValues: Partial<Role>
}

function RoleForm (props: RoleFormProps) {
  const { initialValues } = props
  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const isCurrentRole = auth.context.roleId === id
  const editable = auth.hasPermission('write:role') && !isCurrentRole

  const form = useCustomForm({
    disabled: !editable,
    initialValues,
    initializeField: createFieldInitializer(RoleSchema),
    validate: createFormValidator(RoleSchema),
    validateField: createFieldValidator(RoleSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, RoleSchema)

      // Ignore permissions the role is the current user's role.
      if (isCurrentRole) {
        delete finalValues.permissions
      }
      return id > 0
        ? patchRole(id, finalValues)
        : postRole(finalValues)
    }
  })

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? getRoleListLink() : undefined}
    >
      <Stack>
        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'general',
            defaultMessage: 'Général'
          })}
        >
          <NameField />
          <SlugField />
        </FormSection>

        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'permissions',
            defaultMessage: 'Permissions'
          })}
        >
          <PermissionsField />
        </FormSection>

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default RoleForm
